<section class="archive-data" aria-labelledby="archive-data-title">
  <header class="archive-heading">
    <div class="title-row">
      <h2 id="archive-data-title">Archive Data</h2>
      <span class="admin-badge">GLOBAL ADMIN ONLY</span>
    </div>
    <button
      type="button"
      class="icon-button"
      title="Refresh archive status"
      aria-label="Refresh archive status"
      [disabled]="loading || submitting"
      (click)="refresh()"
    >
      <span class="material-icons" aria-hidden="true">refresh</span>
    </button>
  </header>

  <p class="summary-copy">
    Archive jurisdiction-owned applications while retaining original references
    and application records for audit.
  </p>
  <div *ngIf="loading && !summary" class="notice info" role="status">
    Loading archive details...
  </div>
  <div *ngIf="error" class="notice danger" role="alert">
    <span class="material-icons" aria-hidden="true">error_outline</span
    ><span>{{ error }}</span>
  </div>

  <ng-container *ngIf="summary as data">
    <div *ngIf="data.executionNotice" class="notice info">
      <span class="material-icons" aria-hidden="true">info_outline</span
      ><span>{{ data.executionNotice }}</span>
    </div>
    <div *ngIf="data.mode !== 0" class="notice warning">
      <span class="material-icons" aria-hidden="true">lock_outline</span>
      <span
        >This jurisdiction is in <strong>Live</strong> mode. Archiving is
        disabled. Switch to Test mode on the General tab to enable it.</span
      >
    </div>
    <div *ngIf="!data.executionAvailable" class="notice warning">
      <span class="material-icons" aria-hidden="true"
        >pause_circle_outline</span
      >
      <span>{{ data.executionUnavailableReason }}</span>
    </div>

    <div class="eligibility">
      <span class="metric-label">Applications eligible for archival</span>
      <strong>{{ data.eligibleApplications | number }}</strong>
      <span class="mode" [class.live]="data.mode !== 0">{{
        data.mode === 0 ? 'Test mode' : 'Live mode'
      }}</span>
    </div>
    <div class="eligibility">
      <span class="metric-label">Inspections eligible for archival</span>
      <strong>{{ data.eligibleInspections | number }}</strong>
    </div>
    <div class="eligibility">
      <span class="metric-label">Registrations (renewals) eligible for archival</span>
      <strong>{{ data.eligibleRegistrations | number }}</strong>
    </div>
    <div class="eligibility">
      <span class="metric-label">Contractor registrations eligible for archival</span>
      <strong>{{ data.eligibleContractorRegistrations | number }}</strong>
    </div>

    <div *ngIf="activeRun as run" class="current-run" aria-live="polite">
      <div class="run-heading">
        <h3>Current archive</h3>
        <span class="status" [attr.data-status]="run.status">{{
          run.status
        }}</span>
      </div>
      <div
        *ngIf="run.status === 'Running'"
        class="progress"
        role="progressbar"
        aria-label="Archive in progress"
      >
        <div class="progress-indeterminate"></div>
      </div>
      <p>
        {{ run.applicationsArchived | number }} applications,
        {{ run.inspectionsArchived | number }} inspections,
        {{ run.registrationsArchived | number }} renewal registrations, and
        {{ run.contractorRegistrationsArchived | number }} contractor
        registrations archived
      </p>
      <dl>
        <dt>Batch ID</dt>
        <dd class="identifier">{{ run.id }}</dd>
        <dt>Queued by</dt>
        <dd>{{ run.createdBy }}</dd>
        <dt>Queued</dt>
        <dd>{{ run.createdOn | date: 'medium' }}</dd>
      </dl>
    </div>

    <div
      *ngIf="!activeRun && data.mode === 0 && data.eligibleApplications > 0"
      class="notice danger"
    >
      <span class="material-icons" aria-hidden="true">warning_amber</span>
      <span
        >This operation will remove
        <strong>{{ data.eligibleApplications | number }} applications</strong>,
        <strong>{{ data.eligibleInspections | number }} inspections</strong>,
        <strong>{{ data.eligibleRegistrations | number }} renewal registrations</strong>, and
        <strong>{{ data.eligibleContractorRegistrations | number }} contractor registrations</strong>
        from active views. Original data is retained.
        There is no restore action.</span
      >
    </div>
    <p
      *ngIf="!activeRun && data.eligibleApplications === 0"
      class="empty-state"
    >
      No applications are eligible for archival.
    </p>

    <div class="archive-actions">
      <button
        type="button"
        class="archive-button"
        [disabled]="!canArchive"
        (click)="openConfirmation(confirmationDialog)"
      >
        <span class="material-icons" aria-hidden="true">archive</span>Archive
        Data
      </button>
    </div>

    <section
      *ngIf="data.runs.length"
      class="history"
      aria-labelledby="archive-history-title"
    >
      <h3 id="archive-history-title">Archive History</h3>
      <article *ngFor="let run of data.runs" class="history-run">
        <div class="run-heading">
          <span class="identifier">{{ run.id }}</span
          ><span class="status" [attr.data-status]="run.status">{{
            run.status
          }}</span>
        </div>
        <div
          *ngIf="run.status === 'Completed'"
          class="progress completed-progress"
          role="progressbar"
          aria-label="Archive completed"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow="100"
        >
          <div></div>
        </div>
        <dl>
          <dt>Requested by</dt>
          <dd>{{ run.createdBy }}</dd>
          <dt>Queued</dt>
          <dd>{{ run.createdOn | date: 'medium' }}</dd>
          <dt>Started</dt>
          <dd>{{ run.startedOn ? (run.startedOn | date: 'medium') : '-' }}</dd>
          <dt>{{ run.status === 'Failed' ? 'Failed' : 'Completed' }}</dt>
          <dd>
            {{ run.completedOn ? (run.completedOn | date: 'medium') : '-' }}
          </dd>
          <dt>Applications archived</dt>
          <dd>{{ run.applicationsArchived | number }}</dd>
          <dt>Inspections archived</dt>
          <dd>{{ run.inspectionsArchived | number }}</dd>
          <dt>Renewal registrations archived</dt>
          <dd>{{ run.registrationsArchived | number }}</dd>
          <dt>Contractor registrations archived</dt>
          <dd>{{ run.contractorRegistrationsArchived | number }}</dd>
          <dt *ngIf="run.queueId">Queue ID</dt>
          <dd *ngIf="run.queueId" class="identifier">{{ run.queueId }}</dd>
        </dl>
        <div *ngIf="run.errorMessage" class="notice danger" role="alert">
          <span class="material-icons" aria-hidden="true">error_outline</span
          ><span
            >{{ run.errorMessage }} Previously archived records remain
            archived.</span
          >
        </div>
      </article>
    </section>
  </ng-container>
</section>

<ng-template #confirmationDialog let-dialog>
  <div class="modal-header">
    <h2 id="archive-confirm-title" class="modal-title">Confirm Archive Data</h2>
    <button
      type="button"
      class="icon-button"
      title="Close confirmation"
      aria-label="Close confirmation"
      [disabled]="submitting"
      (click)="dialog.dismiss()"
    >
      <span class="material-icons" aria-hidden="true">close</span>
    </button>
  </div>
  <div class="modal-body">
    <p>This action cannot be undone from the UI. Type this sentence exactly:</p>
    <p class="confirmation-sentence">{{ confirmationSentence }}</p>
    <label for="archive-confirmation">Confirmation</label>
    <input
      id="archive-confirmation"
      class="confirmation-input"
      [(ngModel)]="confirmation"
      autocomplete="off"
      [disabled]="submitting"
    />
    <p
      *ngIf="confirmation && confirmation !== confirmationSentence"
      class="validation-message"
      role="status"
    >
      The confirmation does not match.
    </p>
  </div>
  <div class="modal-footer">
    <button
      type="button"
      class="cancel-button"
      [disabled]="submitting"
      (click)="dialog.dismiss()"
    >
      Cancel</button
    ><button
      type="button"
      class="archive-button"
      [disabled]="confirmation !== confirmationSentence || !canArchive"
      (click)="confirmArchive()"
    >
      {{ submitting ? 'Queuing...' : 'Confirm Archive' }}
    </button>
  </div>
</ng-template>
